import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import dayjs from 'shared/helpers/dayjs';
import useClickOutside from 'shared/hooks/useClickOutside';
import { Arrow, General, Time } from 'icons/untitledui';
import { SecondaryLightIcon } from 'theme/components/Buttons.style';
import { calendarPopperSx } from 'dashboard/styles/Filters.style';

import * as Styled from 'events/styles/EventsHomePage.style';

interface DateSearchPillProps {
  selectedDate: dayjs.Dayjs;
  onSelectDate: (date: Date) => void;
  onSearchClick: () => void;
  onCalendarToggle?: () => void;
}

export default function DateSearchPill({
  selectedDate,
  onSelectDate,
  onSearchClick,
  onCalendarToggle,
}: DateSearchPillProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const calendarAnchorRef = useRef<HTMLButtonElement>(null);
  const calendarPopperRef = useRef<HTMLDivElement>(null);

  const closeDatePicker = () => setDatePickerOpen(false);

  const handleCalendarClick = () => {
    onCalendarToggle?.();
    setDatePickerOpen((prev) => !prev);
  };

  const handleCalendarDateChange = (date: Date | null) => {
    if (!date) return;
    onSelectDate(date);
    setDatePickerOpen(false);
  };

  useClickOutside(
    [calendarAnchorRef, calendarPopperRef],
    closeDatePicker,
    datePickerOpen
  );

  return (
    <Styled.TopPill>
      <SecondaryLightIcon
        type="button"
        $size="small"
        onClick={onSearchClick}
        aria-label={t('eventsHome.searchAriaLabel')}
      >
        <General.SearchSm />
      </SecondaryLightIcon>
      <SecondaryLightIcon
        ref={calendarAnchorRef}
        type="button"
        $size="small"
        onClick={handleCalendarClick}
        aria-label={t('eventsHome.calendarAriaLabel')}
      >
        <Time.Calendar />
      </SecondaryLightIcon>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDatePicker
          value={selectedDate.toDate()}
          onChange={handleCalendarDateChange}
          maxDate={dayjs().endOf('day').toDate()}
          open={datePickerOpen}
          onClose={closeDatePicker}
          slots={{
            leftArrowIcon: Arrow.ChevronLeft,
            rightArrowIcon: Arrow.ChevronRight,
          }}
          slotProps={{
            textField: {
              sx: {
                position: 'absolute',
                width: 0,
                height: 0,
                overflow: 'hidden',
                '& .MuiOutlinedInput-root': { display: 'none' },
              },
            },
            popper: {
              ref: calendarPopperRef,
              anchorEl: calendarAnchorRef.current,
              placement: 'bottom-end',
              sx: calendarPopperSx,
            },
          }}
        />
      </LocalizationProvider>
    </Styled.TopPill>
  );
}
